class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => { if (event.code === 'Escape') this.close(); });
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('submit', (event) => { if (this.busy) event.preventDefault(); });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-ac-input]')) return;
      const control = event.target.closest('.ac-quantity');
      const quantity = Number(event.target.value);
      if (!Number.isInteger(quantity) || quantity < 0) { event.target.value = Math.round(Number(control.dataset.actual) / Number(control.dataset.scale)); return; }
      this.changeQuantity(control.dataset.key, Math.round(quantity * Number(control.dataset.scale)));
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-areliaa-cart-open],#cart-icon-bubble');
      if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(trigger);
      this.refresh();
    });
    document.addEventListener('keydown', (event) => {
      if (event.code !== 'Space' || !event.target.closest('[data-areliaa-cart-open],#cart-icon-bubble')) return;
      event.preventDefault(); this.open(event.target); this.refresh();
    });
    this.initCarousel();
  }
  connectedCallback() { this.syncCount(); }
  onClick(event) {
    const button = event.target.closest('button,[data-ac-close]');
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-ac-close')) { this.close(); return; }
    if (button.hasAttribute('data-ac-add')) { this.addRecommendation(button); return; }
    if (button.hasAttribute('data-ac-remove')) { this.changeQuantity(button.dataset.key, Number(button.dataset.quantity)); return; }
    if (button.hasAttribute('data-ac-step')) {
      const control = button.closest('.ac-quantity');
      const quantity = Math.max(0, Number(control.querySelector('input').value) + Number(button.dataset.acStep));
      this.changeQuantity(control.dataset.key, Math.round(quantity * Number(control.dataset.scale)));
      return;
    }
    if (button.hasAttribute('data-ac-prev')) this.moveSlide(-1);
    if (button.hasAttribute('data-ac-next')) this.moveSlide(1);
    if (button.hasAttribute('data-ac-slide')) this.goToSlide(Number(button.dataset.acSlide));
  }
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    const panel = this.querySelector('.ac-panel');
    if (panel) trapFocus(panel, panel);
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }
  setActiveElement(element) { this.activeElement = element; }
  getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }]; }
  getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
  getSectionInnerHTML(html, selector = '.shopify-section') { return this.getSectionDOM(html, selector)?.innerHTML || ''; }
  renderContents(state) {
    if (state?.sections?.['cart-drawer']) { this.paint(state.sections['cart-drawer']); this.open(); return; }
    return this.refresh(true);
  }
  paint(html) {
    const next = this.getSectionDOM(html, '#CartDrawer');
    const current = this.querySelector('#CartDrawer');
    if (!next || !current) throw new Error('The cart could not be refreshed. Please try again.');
    const hadFocus = this.contains(document.activeElement);
    const scrollTop = this.querySelector('.ac-items')?.scrollTop || 0;
    current.replaceChildren(...next.childNodes);
    current.dataset.cartCount = next.dataset.cartCount;
    this.classList.toggle('is-empty', Number(next.dataset.cartCount) === 0);
    this.syncCount(); this.initCarousel();
    const items = this.querySelector('.ac-items');
    if (items) items.scrollTop = scrollTop;
    if (hadFocus && this.classList.contains('active')) { removeTrapFocus(); const panel = this.querySelector('.ac-panel'); trapFocus(panel, panel); }
  }
  syncCount() {
    const count = this.querySelector('#CartDrawer')?.dataset.cartCount || '0';
    document.querySelectorAll('.areliaa-header__cart-count').forEach((element) => { element.textContent = count; });
  }
  async refresh(open = false) {
    if (this.refreshPromise) { await this.refreshPromise; if (open) this.open(); return; }
    this.refreshPromise = (async () => {
      const url = new URL(window.routes?.cart_url || '/cart', location.origin);
      url.searchParams.set('section_id', 'cart-drawer');
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('The cart could not be refreshed. Please try again.');
      this.paint(await response.text());
      if (open) this.open();
    })();
    try { await this.refreshPromise; } catch (error) { this.showError(error.message); } finally { this.refreshPromise = null; }
  }
  async mutate(url, payload, focusLabel) {
    if (this.busy) return;
    this.busy = true; this.setAttribute('aria-busy', 'true'); this.showError('');
    try {
      if (this.refreshPromise) await this.refreshPromise;
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ ...payload, sections: ['cart-drawer'], sections_url: location.pathname }) });
      const state = await response.json();
      if (!response.ok || state.errors || state.status >= 400) throw new Error(state.description || state.errors || 'The cart could not be updated. Please try again.');
      if (state.sections?.['cart-drawer']) this.paint(state.sections['cart-drawer']); else await this.refresh();
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      if (focusLabel) this.querySelector(`[aria-label="${CSS.escape(focusLabel)}"]`)?.focus({ preventScroll: true });
      const status = this.querySelector('#CartDrawer-LiveRegionText'); if (status) status.textContent = 'Cart updated';
    } catch (error) { this.showError(error.message); } finally { this.busy = false; this.removeAttribute('aria-busy'); }
  }
  changeQuantity(key, quantity) {
    const focused = document.activeElement;
    const label = this.contains(focused) ? focused.getAttribute('aria-label') : null;
    return this.mutate(window.routes?.cart_change_url || '/cart/change.js', { id: key, quantity }, label);
  }
  addRecommendation(button) { return this.mutate(window.routes?.cart_add_url || '/cart/add.js', { items: [{ id: Number(button.dataset.acAdd), quantity: 1 }] }); }
  showError(message) { const errors = this.querySelector('#CartDrawer-CartErrors'); if (errors) errors.textContent = typeof message === 'string' ? message : 'The cart could not be updated. Please try again.'; }
  initCarousel() {
    const track = this.querySelector('.ac-track'); if (!track) return;
    const slides = Array.from(track.children), dots = this.querySelector('.ac-dots'); if (!dots) return;
    dots.replaceChildren(...slides.map((_, index) => { const button = document.createElement('button'); button.type = 'button'; button.className = 'ac-dot'; button.dataset.acSlide = index; button.setAttribute('aria-label', `Recommendation ${index + 1}`); return button; }));
    this.querySelector('.ac-nav').hidden = slides.length <= 1;
    track.addEventListener('scroll', () => this.updateCarousel(), { passive: true }); this.updateCarousel();
  }
  updateCarousel() {
    const track = this.querySelector('.ac-track'); if (!track) return;
    const index = Math.round(track.scrollLeft / ((track.children[0]?.getBoundingClientRect().width || track.clientWidth) + 10));
    this.querySelectorAll('.ac-dot').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    const previous = this.querySelector('[data-ac-prev]'), next = this.querySelector('[data-ac-next]');
    if (previous) previous.disabled = index <= 0; if (next) next.disabled = index >= track.children.length - 1; this.slideIndex = index;
  }
  moveSlide(direction) { this.goToSlide((this.slideIndex || 0) + direction); }
  goToSlide(index) {
    const track = this.querySelector('.ac-track'); if (!track) return;
    const slide = track.children[Math.max(0, Math.min(index, track.children.length - 1))];
    if (slide) track.scrollTo({ left: slide.offsetLeft - track.children[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
}
customElements.define('cart-drawer', CartDrawer);
class CartDrawerItems extends CartItems {
  onChange() { /* CartDrawer delegates the quantity controls. */ }
  onCartUpdate() { return this.closest('cart-drawer')?.refresh(); }
  getSectionsToRender() { return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' }]; }
}
customElements.define('cart-drawer-items', CartDrawerItems);
